import React, { useEffect, useMemo } from "react";
import { Units, useUnitsComponent } from "../../Styles";
import { useTranslation } from "react-i18next";
import { useAppSelector } from "../../Store";
import { Graph_QL } from "../../Services";
import { useParams } from "react-router";
import { useAnimal } from "../../Services/Agrisys_GqlDataCaller/AnimalDataCalls/useAnimal";
import { useSection } from "../../Services/Agrisys_GqlDataCaller/LocationFeedingCalls/useSections";
import { AnimalInfo } from "../../Services/Agrisys_GqlDataCaller/AnimalDataCalls/GQLCalls/getAnimalCall";
import { useMilestones } from "../../Services/Agrisys_GqlDataCaller/MilestoneDataCalls/useMilestone";
import toCamelCase from "./useCamelCase";

// Progress circles on feeding
export type DefaultProgressCircle = {
  progressCircleId: number;
  header: string;
  unit: JSX.Element;
  progressed: number;
  total: number;
};

interface useDefaultProgressCirclesProps {
  system: number;
}

const useDefaultProgressCircles = ({
  system = 1,
}: useDefaultProgressCirclesProps): DefaultProgressCircle[] => {
  const getUnit = useUnitsComponent();
  const { t } = useTranslation();

  const { systemId: urlSystemId } = useParams(); // TODO routing changes in order to get a correct system info
  const systemId = urlSystemId ? +urlSystemId : system;

  const farm = useAppSelector(({ activeData }) => activeData.farm) ?? "";
  const domain = useMemo(() => Graph_QL.ObjToString({ domain: farm }), [farm]);
  const connection = useMemo(() => Graph_QL.create({ domain: farm }), [farm]);

  const { getAnimalAtLocation, call: getAnimals } = useAnimal(connection);
  const { call: getLfSectionsInfo, data: lfSectionsState } =
    useSection(connection);
  const { call: getMilestoneInfo, data: milestonesState } =
    useMilestones(connection);

  useEffect(() => {
    getLfSectionsInfo({ ids: [systemId] }, connection);
    getMilestoneInfo({ fetchAmount: 50 });
    // eslint-disable-next-line
  }, []);

  useEffect(() => { 
    if (lfSectionsState[domain] && lfSectionsState[domain].data) {
      const locationsIds = lfSectionsState[domain].data
        .flatMap((section) => section.section_locations)
        .map((location) => location.location_id);
      getAnimals({ ids: locationsIds }, connection);
    }
  }, [lfSectionsState, domain, getAnimals, connection]);

  // Find all sections in system
  const sections = useMemo(
    () => (lfSectionsState[domain] ? lfSectionsState[domain].data.filter((section => section.system_id === systemId)) : undefined),
    [domain, lfSectionsState, systemId]
  );

  // Find all locations in system
  const locationsIds = useMemo(() => {
    const accumulator: number[] = [];
    return sections?.reduce(
      (accumulator, currentSection) =>
        accumulator.concat(
          currentSection.section_locations.map(
            (location) => location.location_id
          )
        ),
      accumulator
    );
  }, [sections]);

  // Find all animals in system
  const animalsAtLocations = useMemo(() => {
    return locationsIds?.flatMap((locationId) =>
      getAnimalAtLocation(locationId, domain)
    );
  }, [locationsIds, domain, getAnimalAtLocation]);

  // Number of animals in system
  const totalNumberOfAnimals = animalsAtLocations?.length ?? 0;

  // #region | Feed Consumption
  const totalToday =
    animalsAtLocations?.reduce((accum, animal: AnimalInfo) => {
      return (
        accum +
        (animal.consumptions && animal.consumptions.length
          ? animal.consumptions[0].total ?? 0
          : 0) /
          1000
      );
      
    }, 0) ?? 0;
  
  const totalYesterday =
    animalsAtLocations?.reduce((accum, animal: AnimalInfo) => {
      return (
        accum +
        (animal.consumptions && animal.consumptions.length
          ? animal.consumptions[1].total ?? 0
          : 0) /
          1000
      );
    }, 0) ?? 0;

  const remainingToday =
    animalsAtLocations?.reduce((accum, animal: AnimalInfo) => {
      return (
        accum +
        (animal.consumptions && animal.consumptions.length
          ? animal.consumptions[0].progressed ?? 0
          : 0) /
          1000
      );
    }, 0) ?? 0;

  const remainingYesterday =
    animalsAtLocations?.reduce((accum, animal: AnimalInfo) => {
      return (
        accum +
        (animal.consumptions && animal.consumptions.length
          ? animal.consumptions[1].progressed ?? 0
          : 0) /
          1000
      );
    }, 0) ?? 0;

  const FEED_BALANCE = 0.2; //TODO get from settings?

  const notFedLastDays =
    animalsAtLocations?.filter((animal: AnimalInfo) => {
      return (
        remainingToday <
          FEED_BALANCE *
            (animal.consumptions && animal.consumptions.length
              ? animal.consumptions[0].progressed ?? 0
              : 0) &&
        remainingYesterday <
          FEED_BALANCE *
            (animal.consumptions && animal.consumptions.length
              ? animal.consumptions[1].progressed ?? 0
              : 0)
      );
    }).length ?? 0;
  // #endregion

  // #region | Milestones
  const milestones = useMemo(() => {
    if (milestonesState && milestonesState[domain]) {
      return milestonesState[domain].data;
    }
  }, [domain, milestonesState]);
  // #endregion

  const baseCircles: DefaultProgressCircle[] = [
    {
      progressCircleId: 1,
      header: t("feed.general.remainingToday", {
        defaultValue: "remaining today",
      }),
      unit: getUnit(Units.kG_grey),
      progressed: +(totalToday - remainingToday).toFixed(2),
      total: +totalToday.toFixed(2),
    },
    {
      progressCircleId: 2,
      header: t("feed.general.remainingYesterday", {
        defaultValue: "remaining yesterday",
      }),
      unit: getUnit(Units.kG_grey),
      progressed: +(totalToday - remainingYesterday).toFixed(2),
      total: +totalYesterday.toFixed(2)
    },
    {
      progressCircleId: 3,
      header: t("feed.general.waterUse", { defaultValue: "water use today" }),
      unit: getUnit(Units.kG_grey),
      progressed: +(totalToday - remainingToday).toFixed(2),
      total: +totalToday.toFixed(2),
    },
    {
      progressCircleId: 4,
      header: t("feed.general.notFed", { defaultValue: "not fed last 2 days" }),
      unit: getUnit(Units.animalTotal_grey),
      progressed: notFedLastDays,
      total: totalNumberOfAnimals,
    },
  ];

  const milestoneCircles: DefaultProgressCircle[] = (milestones ?? []).map(
    (milestone, index) => {
      const milestoneKey = toCamelCase(milestone.name);
      return {
        progressCircleId: 5 + index,
        header: t(`milestones.defaultMilestones.${milestoneKey}`, {
          defaultValue: milestone.name,
        }),
        unit: getUnit(Units.animalTotal_grey),
        progressed:
          animalsAtLocations?.filter(
            (animal: AnimalInfo) =>
              animal.milestone?.milestone_id === milestone.milestone_id
          ).length ?? 0,
        total: totalNumberOfAnimals,
      };
    }
  );

  return [...baseCircles, ...milestoneCircles];
};

export default useDefaultProgressCircles;
